import type {Type} from '../DataTypeDefinition';
import type {AssetMap, AssetType} from '../Asset/Asset';
import type {ComponentType, ComponentMap} from '../Component';
import type {NormalizedAtomConfig, AtomConfig} from '../Atom';
import type {
  Qualified,
  QualifiedMap,
  IconName,
  TypeName,
  Identifier,
  SiteModuleDependencyName,
} from '@packages/systems/wfdl-Qualified';
import type {
  NormalizedTypedValue,
  TypedValue,
  SafeTypedValue,
} from '../EnvTypes';
import type {
  ElementPresetType,
  SafeElementPresetType,
} from '../ElementPreset/ElementPreset';
import type {IconProps} from './PluginIconTypes';
import type {ComponentName} from '../Component/Names';
import type {ElementPresetName} from '../ElementPreset';
import type {ValuePresetName, ValuePresetType} from '../ValuePreset';
import {type TypeImplementation} from '../TypeImplementation/TypeImplementation';
import {type SiteModule} from '@packages/systems/plugin-modules-utils';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {StyleBlockId} from '@packages/systems/style/types';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import type {
  SiteModuleDependencyMap,
  SiteModuleDependencyType,
} from '../SiteModules';

export type DataTypes = QualifiedMap<ComponentName, Type>;

export type QualifiedElementPreset = Qualified<ElementPresetName>;
export type QualifiedElement = Qualified<ComponentName>;

export type QualifiedMapElementPresets = QualifiedMap<
  ElementPresetName,
  ElementPresetType
>;

export type QualifiedMapValuePresets = QualifiedMap<
  ValuePresetName,
  ValuePresetType
>;

/**
 * Plugin import strategy
 */
export const IMPORT_STRATEGY = {
  /**
   * This is the _default_ import strategy used today for Plugin primitives
   * that are directly referenced by qualified id.
   *
   * e.g. if an Element Preset renders a Plugin Component,
   * we simply render the Plugin Component.
   */
  REFERENCE: 'REFERENCE',

  /**
   * Plugin primitives are copied to the Site data and tagged
   * with their origin (a qualified ID).
   *
   * e.g. if an Element Preset renders a Plugin Component,
   * we copy the Plugin Component to the Site data (as a User Component)
   * and tag it with origin.
   */
  VALUE: 'VALUE',
} as const;

export type PluginImportStrategy =
  (typeof IMPORT_STRATEGY)[keyof typeof IMPORT_STRATEGY];

export type PluginStyleVariables = Record<
  StyleVariables.VariableID,
  StyleVariables.AnyVariableType
>;

export type PluginStyleVariableCollections = Record<
  StyleVariables.VariableCollectionId,
  StyleVariables.VariableCollection
>;

export type PluginStyleBlocks = Record<StyleBlockId, StyleBlock>;

/**
 * Mapping of plugin ID to slug.
 *
 * Typically a Plugin that has a slug was installed from a Library with a
 * matching ID.
 */
type PluginSlugMap = Readonly<{
  [pluginId: string]: string;
}>;

export type InternalPlugins = {
  __DEPRECATED__dataTypes: {
    [key: string]: TypeImplementation;
  };
  assets: AssetMap;
  components: ComponentMap;
  atoms: QualifiedMap<ComponentName, NormalizedAtomConfig>;
  elementPresets: QualifiedMapElementPresets;
  valuePresets: QualifiedMapValuePresets;
  dataTypes: QualifiedMap<TypeName, TypeImplementation>;
  elementTypeToPluginNameMap: {
    [key: string]: string;
  };
  values: QualifiedMap<Identifier, NormalizedTypedValue>;
  icons: QualifiedMap<IconName, React.ComponentType<IconProps>>;
  importStrategyMap: {
    [pluginId: string]: PluginImportStrategy;
  };
  styleVariables: {
    [pluginId: string]: PluginStyleVariables;
  };
  styleVariableCollections: {
    [pluginId: string]: PluginStyleVariableCollections;
  };
  styleBlocks: {
    [pluginId: string]: PluginStyleBlocks;
  };
  slugMap: PluginSlugMap;
  siteModuleDependencies: QualifiedMap<
    SiteModuleDependencyName,
    SiteModuleDependencyType
  >;
};

/**
 * Webflow Plugin
 */
export type PrivilegedPluginType = {
  /**
   * Assets provided by the plugin for reuse.
   */
  assets?: PluginAssetMap;

  /**
   * Atoms provided by the plugin, keyed by the Atom name. Atoms share a global namespace with other plugin's Atoms. Consequently Atoms often have their plugin name prefixed, e.g. `MyPluginAtom`.
   *
   * ```javascript
   * // index.js in Basic
   * import Block from './Block.jsx';
   * import Blockquote from './Blockquote.jsx';
   *
   * export const atoms = {
   *   Block,
   *   Blockquote,
   * }
   * ```
   */
  atoms?: {
    [key: string]: AtomConfig;
  };
  /**
   * Components provided by the plugin. Components are in active development and will allow you to reuse Atoms with different configurations.
   */
  components?: {
    [key: string]: ComponentType;
  };
  /**
   * elementPresets are preset configurations of Atoms found in the Add Tab & Finder. elementPreset keys share a global namespace amongst other elementPresets from other plugins.
   */
  elementPresets?: {
    [key: string]: ElementPresetType;
  };
  /**
   * valuePresets are preset configurations of Flow Logic Blocks found in the Logic Flow tab. valuePresets keys share a global namespace amongst other valuePresets from other plugins.
   */
  valuePresets?: {
    [key: string]: ValuePresetType;
  };
  /**
   * The stateAtom is used in combination with the `uiStateOnSelected` Atom API.
   *
   * It indicates by Atom type name the Atom whoms node.data contains state for the plugin.
   */
  stateAtom?: string;
  /**
   * Plugin exported dataTypes allow you to extend Webflow with a `Type` for use in another Atom or context. Let's promote our `BPM` type from above to a plugin type to use it on another Atom! Plugins allow you to provide a new export, `dataTypes`!
   *
   * ```javascript
   * // plugins/MyPlugin/index.js
   * export const dataTypes = {
   *   GPS: {
   *     type: 'Custom',
   *     render: // GPS Settings RENDER,
   *     reducer: // GPS Settings REDUCER,
   *   },
   * };
   * ```
   *
   * This would allow Atoms in other plugins to consume your `Type`.
   *
   * ```javascript
   * const SomeAtom = {
   *   dataType: {
   *    type: 'Record',
   *    fields: { gpsCoordinates: { type: 'TypeConstructor', val: ['MyPlugin', 'GPS'] }}
   *  }
   * }
   * ```
   *
   * > You are unable to export another Plugins published type, this is by design and Flow should prevent you from doing it.
   */
  dataTypes?: {
    [key: string]: TypeImplementation;
  };
  /**
   * Provide values that can be referenced by expressions.
   */
  values?: {
    [key: string]: TypedValue;
  };
  /**
   * siteModules allow you to provide runtime scripts that will also be bundled and deployed with the published site
   */
  siteModules?: Array<SiteModule>;
  /**
   * External npm dependencies required by your site modules. Used only by in browser built code components.
   */
  siteModuleDependencies?: SiteModuleDependencyMap;
  /**
   * Plugin icons
   */
  icons?: {
    [key: string]: React.ComponentType<IconProps>;
  };
  /**
   * Plugin import strategy that defines how to import Plugin primitives to the Site
   */
  importStrategy?: PluginImportStrategy;
  /**
   * Plugin style variables
   */
  styleVariables?: PluginStyleVariables;
  /**
   * Plugin style variable collections
   */
  styleVariableCollections?: PluginStyleVariableCollections;
  /**
   * Plugin style blocks
   */
  styleBlocks?: PluginStyleBlocks;
  /**
   * If provided, a globally unique, quasi human-readable identifier.
   *
   * Typically derived from a `LibraryVersionedResource` that created this
   * plugin.
   */
  slug?: string;
};

export type PluginID = string;

export type PrivilegedPluginDeclaration = {
  type: 'PRIVILEGED';
  definition: PrivilegedPluginType;
};

type PluginDeclaration = {
  type: 'PLUGIN';
  definition: PluginType;
};

export type PluginDeclarations =
  | PluginDeclaration
  | PrivilegedPluginDeclaration;

/**
 * Map of Plugin IDs to their declarations which include the level of
 * access the Plugin will have.
 *
 * ```jsx
 * {
 *  Builtin: { type: 'PRIVILEGED', ...coupled },
 *  ThirdParty: { type: 'PLUGIN', ...safe }
 * }
 * ```
 */
export type PluginDeclarationMap = Partial<
  Record<PluginID, PluginDeclarations>
>;

type PluginAssetMap = {
  [key: string]: AssetType;
};

type PluginComponentMap = {
  [key: string]: ComponentType;
};

export type PluginType = {
  assets?: PluginAssetMap;
  components?: PluginComponentMap;
  styleVariables?: PluginStyleVariables;
  styleVariableCollections?: PluginStyleVariableCollections;
  styleBlocks?: PluginStyleBlocks;
  elementPresets?: {
    [key: string]: SafeElementPresetType;
  };
  valuePresets?: {
    [key: string]: ValuePresetType;
  };
  values?: {
    [key: string]: SafeTypedValue;
  };
  importStrategy?: PluginImportStrategy;
  /**
   * If provided, a globally unique, quasi human-readable identifier.
   *
   * Typically derived from a `LibraryVersionedResource` that created this
   * plugin.
   */
  slug?: string;
  /**
   * External npm dependencies required by this plugin's site modules. Used only by in browser built code components.
   */
  siteModuleDependencies?: SiteModuleDependencyMap;
};

export function PrivilegedPlugin(
  definition: PrivilegedPluginType
): PrivilegedPluginDeclaration {
  return {type: 'PRIVILEGED', definition};
}

export function Plugin(definition: PluginType): PluginDeclaration {
  return {type: 'PLUGIN', definition};
}
